import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./Thread.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "../../hooks/useRouter";

// component
import Modal from "../Modal/Modal";
import SearchBar from "../SearchBar/SearchBar";

// utility, api and helper functions
import { toast } from "react-toastify";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import {
  getUserThreadsConversation,
  updateUserThreadRequest,
} from "../../store/apiActions";
import { fetchThreadsGeneric } from "../../utils/threadUtils";
import { useConstants } from "../../constant/constantProvider";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/useRbac";
import { sessionManager } from "../../utils/utility";
import useIsMobile from "../../hooks/useIsMobile";
import { setSelectedThreadName } from "../../store/actions";
import { RBAC_ENABLED } from "../../constant/globalConstants";
import { AGENT_TYPES } from "../../constant/keyConstants";

// assets
import renameIcon from "../../assets/new-images/rename-icon.svg";
import deleteIcon from "../../assets/new-images/delete-icon.svg";
import downArrow from "../../assets/new-images/down-arrow-icon.svg";
import pinIcon from "../../assets/new-images/pin-icon.svg";
import unpinIcon from "../../assets/new-images/unpin-icon.svg";
import threeDotsIcon from "../../assets/images/three-dots-icon.svg";

import { getThreadGroup } from "../../utils/dateUtils";

function Threads({
  // Configuration props
  config = {
    getThreadsAction: getUserThreadsConversation,
    deleteThreadAction: updateUserThreadRequest,
    renameThreadAction: updateUserThreadRequest,
    pinThreadAction: updateUserThreadRequest,
    sessionStorageKey: "sessionId",
    agentType: "CHAT_GENIE",
    reduxStateKey: "userThreadDetails",
    threadFieldNames: {
      sessionId: "session_id",
      threadTitle: "thread_title",
    },
    enableGrouping: true,
  },
  showSearchBar = false,
  isCollapsedHistory = false,
  setIsCollapsedHistory,
  maxPinThreads,
}) {
  const toggleHistory = () => {
    if (setIsCollapsedHistory) {
      setIsCollapsedHistory(!isCollapsedHistory);
    }
  };
  // Extract config values FIRST before using them
  const {
    getThreadsAction,
    deleteThreadAction,
    renameThreadAction,
    pinThreadAction,
    sessionStorageKey,
    agentType,
    reduxStateKey,
    threadFieldNames,
    enableGrouping = true
  } = config;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [threads, setThreads] = useState([]);
  const isMobile = useIsMobile();
  const savedThreadId = sessionManager?.getSessionId(sessionStorageKey) || null;
  const [selectedThreadId, setSelectedThreadId] = useState(savedThreadId);
  const [showModalMore, setShowModalMore] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { emit, subscribe } = useEventEmitter();
  const [openedModelIndex, setOpenedModelIndex] = useState(null);
  const [openedThreadId, setOpenedThreadId] = useState(null);
  const modalRef = useRef(null);
  const [hoveredThreadId, setHoveredThreadId] = useState(null);
  const [hoveredItemIndex, setHoveredItemIndex] = useState(null);
  const selectedThreadRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [showMobileRenameModal, setShowMobileRenameModal] = useState(false);
  const [renameTargetItem, setRenameTargetItem] = useState(null);
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinTargetItem, setPinTargetItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const hasAnyThreads = threads?.length > 0;


  const { username, ugdnId } = useAuth();
  const { rbacData, isRbacLoaded } = useRbac();
  const constants = useConstants();
  const {
    NO_THREADS, HISTORY, RENAME, DELETE, MORE_OPTIONS,
    PIN_CHAT, UNPIN_CHAT, DELETE_CHAT, RENAME_CHAT,
    PIN_CONFIRM_MSG, UNPIN_CONFIRM_MSG, DELETE_CONFIRM_MSG,
    RENAME_INSTRUCTION, PIN, UNPIN, CANCEL, SAVE, PINNED_CHATS, TITLE_EMPTY_ERROR, NO_THREADS_FOUND,
    TODAY, PREVIOUS_7_DAYS, OLDER, SEARCH_HISTORY, NO_HISTORY_AVAILABLE, MAX_PIN_THREADS_REACHED
  } = constants;
  const { localeCode } = constants;

  const handleThreadClick = (item) => {
    const sessionId = item?.[threadFieldNames.sessionId];
    emit("thread", agentType, sessionId);
    if (isMobile) emit("closeSidebar");
    setSelectedThreadId(sessionId);
    dispatch(setSelectedThreadName(item));
    sessionManager.setSessionId(sessionId, sessionStorageKey);
  };

  // Scroll active thread into view
  useEffect(() => {
    if (selectedThreadId && selectedThreadRef.current) {
      selectedThreadRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedThreadId, threads]);

  useEffect(() => {
    const unsubscribe = subscribe("resetSelectedThread", () => {
      setSelectedThreadId(null);
      dispatch(setSelectedThreadName(null));
      sessionManager.setSessionId(null, sessionStorageKey);
    });
    return () => unsubscribe();
  }, [subscribe, sessionStorageKey, dispatch]);

  const closeModal = useCallback(() => {
    setShowModalMore(false);
    setOpenedThreadId(null);
    setOpenedModelIndex(null);
    setIsEditing(false);
  }, []);

  const openModal = useCallback((e, sessionId, itemIndex, currentTitle) => {
    const rect = e.target.getBoundingClientRect();
    const modalHeight = 120;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldPositionAbove = spaceBelow < (modalHeight + 20) && spaceAbove > spaceBelow;

    const topPosition = shouldPositionAbove
      ? rect.top + window.scrollY - modalHeight + 5
      : rect.bottom + window.scrollY + 5;

    setOpenedThreadId(sessionId);
    setOpenedModelIndex(itemIndex);
    setEditTitle(currentTitle);
    setShowModalMore(true);
    setModalPosition({
      top: Math.max(5, topPosition),
      left: rect.left + window.scrollX - (isMobile ? 50 : 10),
    });
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeModal]);

  useEffect(() => {
    if (isEditing && modalRef.current) modalRef.current.focus();
  }, [isEditing]);


  const handleNewChatFun = useCallback(async () => {
    setThreads([]);

    const threads = await fetchThreadsGeneric({
      dispatch,
      getThreadsAction,
      agentType,
      sessionStorageKey,
      sessionManager,
      navigate,
      isMobile,
      hasAccess: () => {
        if (!RBAC_ENABLED) return true;
        if (!agentType) return true;
        if (!rbacData) return false;

        const reverseNameMap = {
          "CHAT_GENIE": "Chat Genie",
          "PROCUREMENT_AI": "Procurement AI",
          "GMT": "GMT",
          "TRANSLATE": "Translate AI",
          "HR_ASSISTANT": "HR Assistant",
          "DEEP_INSIGHTS": "Deep Insights"
        };
        const appName = reverseNameMap[agentType];

        return appName ? !!rbacData[appName] : true;
      },
    });

    const normalizedThreads = Array.isArray(threads) ? threads : [];
    setThreads(normalizedThreads);
    return normalizedThreads;
  }, [
    agentType,
    dispatch,
    getThreadsAction,
    isMobile,
    navigate,
    sessionStorageKey,
    rbacData,
  ]);

  useEffect(() => {
    if (isRbacLoaded) {
      handleNewChatFun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentType, getThreadsAction, isRbacLoaded]);

  useEffect(() => {
    const unsubscribe = subscribe("updateThreads", () => {
      handleNewChatFun();
    });
    return () => unsubscribe();
  }, [subscribe, handleNewChatFun]);

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleRenameItem = useCallback(async (item) => {
    const newTitleValue = editTitle.trim();
    if (!newTitleValue) {
      alert(TITLE_EMPTY_ERROR || "Title cannot be empty.");
      return;
    }

    const sessionIdField = threadFieldNames.sessionId;
    const currentSessionId = item?.[sessionIdField];

    setIsEditing(false);
    setShowModalMore(false);
    setOpenedThreadId(null);
    setOpenedModelIndex(null);
    setEditTitle("");

    const payload = {
      user_id: username,
      ugdn_id: ugdnId,
      [sessionIdField]: currentSessionId,
      session_title: newTitleValue,
      thread_name: newTitleValue,
      new_title: newTitleValue,
      ...(agentType === AGENT_TYPES.DEEP_INSIGHTS && { thread_title: newTitleValue }),
      delete_flag: false,
    };

    try {
      await dispatch(renameThreadAction(payload));

      const storedSessionId = sessionManager.getSessionId(sessionStorageKey);
      if (currentSessionId === storedSessionId) {
        dispatch(setSelectedThreadName({ ...item, [threadFieldNames.threadTitle]: newTitleValue }));
      }

      toast.info(constants.chat.THREAD_RENAME_SUCCESS || "Thread renamed successfully");
      setTimeout(() => handleNewChatFun(), 200);
    } catch (err) {
      toast.error(err?.message || constants.chat.THREAD_RENAME_FAILED || "Rename failed");
    }
  }, [editTitle, threadFieldNames, username, ugdnId, dispatch, renameThreadAction, sessionStorageKey, handleNewChatFun, constants, TITLE_EMPTY_ERROR, agentType]);

  const handleDelete = useCallback(async (item) => {
    if (!item) return;
    const sessionIdField = threadFieldNames.sessionId;
    const currentDeletedId = item?.[sessionIdField];
    const payload = {
      user_id: username,
      [sessionIdField]: currentDeletedId,
      ugdn_id: ugdnId,
      delete_flag: true,
      ...(agentType === AGENT_TYPES.DEEP_INSIGHTS && { delete: true }),
    };

    try {
      await dispatch(deleteThreadAction(payload));

      const storedSessionId = sessionManager.getSessionId(sessionStorageKey);

      if (currentDeletedId === storedSessionId) {
        dispatch(setSelectedThreadName(null));
        emit("clearChat");
      }

      setTimeout(() => {
        handleNewChatFun();
      }, 1000);
      toast.info(constants.chat.THREAD_DELETE_SUCCESS || "Thread deleted successfully");
    } catch (err) {
      toast.error(err?.message || constants.chat.THREAD_DELETE_FAILED || "Delete failed");
    }
  }, [threadFieldNames.sessionId, username, ugdnId, dispatch, deleteThreadAction, sessionStorageKey, handleNewChatFun, emit, constants, agentType]);

  const handlePinToggle = useCallback(async (item) => {
    if (!item) return;
    const sessionIdField = threadFieldNames.sessionId;
    const isCurrentlyPinned = item?.pin_flag;

    if (!isCurrentlyPinned && typeof maxPinThreads === 'number') {
      const currentPinnedCount = threads.filter(t => t.pin_flag).length;
      if (currentPinnedCount >= maxPinThreads) {
        const errorMsg = MAX_PIN_THREADS_REACHED
          ? MAX_PIN_THREADS_REACHED.replace('{max}', maxPinThreads)
          : `max allowed pin thread is ${maxPinThreads}`;
        toast.error(errorMsg);
        return;
      }
    }

    const payload = {
      [sessionIdField]: item?.[sessionIdField],
      pin_flag: !isCurrentlyPinned,
    };
    try {
      await dispatch(pinThreadAction(payload));
      setTimeout(async () => {
        const refreshedThreads = await handleNewChatFun();
        // Keep the shared selected-thread in sync so consumers outside the sidebar
        // (chat page pin, header menu) reflect the new pin state immediately.
        const storedSessionId = sessionManager.getSessionId(sessionStorageKey);
        if (item?.[sessionIdField] !== storedSessionId) return;
        const refreshed = refreshedThreads?.find(
          (thread) => thread?.[sessionIdField] === storedSessionId
        );
        dispatch(setSelectedThreadName(refreshed || { ...item, pin_flag: !isCurrentlyPinned }));
      }, 100);
      toast.info(isCurrentlyPinned
        ? (constants.chat.THREAD_UNPIN_SUCCESS || "Thread unpinned successfully")
        : (constants.chat.THREAD_PIN_SUCCESS || "Thread pinned successfully"));
    } catch (err) {
      toast.error(err?.message || (isCurrentlyPinned
        ? (constants.chat.THREAD_UNPIN_FAILED || "Unpin failed")
        : (constants.chat.THREAD_PIN_FAILED || "Pin failed")));
    }
  }, [threadFieldNames.sessionId, dispatch, pinThreadAction, handleNewChatFun, constants, maxPinThreads, threads, MAX_PIN_THREADS_REACHED, sessionStorageKey]);

  // Stable empty array reference to prevent unnecessary rerenders
  const EMPTY_ARRAY = useMemo(() => [], []);

  // Memoize the selector result to prevent unnecessary rerenders
  const threadResponseData = useSelector(
    (state) => state?.api?.[reduxStateKey]?.data
  );

  const threadResponse = useMemo(() => {
    return threadResponseData ?? EMPTY_ARRAY;
  }, [threadResponseData, EMPTY_ARRAY]);

  useEffect(() => {
    // Handle different response structures
    let threadsArray = [];

    if (Array.isArray(threadResponse)) {
      threadsArray = threadResponse;
    } else if (threadResponse?.threads && Array.isArray(threadResponse.threads)) {
      threadsArray = threadResponse.threads;
    } else if (threadResponse?.sessions && Array.isArray(threadResponse.sessions)) {
      threadsArray = threadResponse.sessions;
    } else if (threadResponse && typeof threadResponse === 'object') {
      // Fallback: try to extract any array from the response
      threadsArray = Object.values(threadResponse).find(Array.isArray) || [];
    }

    if (!threadsArray.length) return;

    setThreads(threadsArray);
    const storedSessionId = sessionManager.getSessionId(sessionStorageKey);
    const sessionIdField = threadFieldNames.sessionId;
    const matchingThread = threadsArray.find(
      (thread) => thread?.[sessionIdField] === storedSessionId
    );
    if (matchingThread) {
      const sessionId = matchingThread[sessionIdField];
      setSelectedThreadId(sessionId);
      dispatch(setSelectedThreadName(matchingThread));
      emit("thread", agentType, storedSessionId);
    }
  }, [threadResponse, sessionStorageKey, threadFieldNames.sessionId, threadFieldNames.threadTitle, agentType, emit, dispatch]);

  // Header operations -> Open Modals in Thread
  useEffect(() => {
    const unsubRename = subscribe("headerOpenRenameModal", (threadObj) => {
      if (!threadObj) return;
      setRenameTargetItem(threadObj);
      setEditTitle(threadObj?.[threadFieldNames.threadTitle] || "");
      setShowMobileRenameModal(true);
    });

    const unsubDelete = subscribe("headerOpenDeleteModal", (threadObj) => {
      if (!threadObj) return;
      setDeleteTargetItem(threadObj);
      setShowDeleteModal(true);
    });

    const unsubPin = subscribe("headerOpenPinModal", (threadObj) => {
      if (!threadObj) return;
      setPinTargetItem(threadObj);
      setShowPinModal(true);
    });

    return () => {
      unsubRename();
      unsubDelete();
      unsubPin();
    };
  }, [subscribe, threadFieldNames.threadTitle]);

  // Filter threads based on search query
  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) {
      return threads;
    }
    const query = searchQuery.toLowerCase().trim();
    return threads.filter((item) => {
      const threadTitle =
        item?.[threadFieldNames?.threadTitle] ||
        item?.thread_title ||
        item?.session_title ||
        "";
      return threadTitle.toLowerCase().includes(query);
    });
  }, [threads, searchQuery, threadFieldNames]);

  // Group threads by date
  const effectiveEnableGrouping = useMemo(() => {
    if (!enableGrouping || !filteredThreads || filteredThreads.length === 0) return false;
    return filteredThreads.some(t => t.edit_time || t.updated_at || t.created_at);
  }, [enableGrouping, filteredThreads]);

  const groupedThreads = useMemo(() => {
    if (!filteredThreads || filteredThreads.length === 0) return {};

    if (!effectiveEnableGrouping) {
      const pinned = filteredThreads.filter(t => t.pin_flag);
      const unpinned = filteredThreads.filter(t => !t.pin_flag);
      return { "all": [...pinned, ...unpinned] };
    }

    // Sort threads by date descending to ensure chronological grouping and ordering
    const sortedThreads = [...filteredThreads].sort((a, b) => {
      const dateA = new Date(a.edit_time || a.updated_at || a.created_at || 0);
      const dateB = new Date(b.edit_time || b.updated_at || b.created_at || 0);
      return dateB.getTime() - dateA.getTime();
    });

    const groups = {};
    groups[PINNED_CHATS || "Pinned Chats"] = [];

    sortedThreads.forEach((thread) => {
      if (thread.pin_flag) {
        groups[PINNED_CHATS || "Pinned Chats"].push(thread);
      } else {
        const dateStr = thread.edit_time || thread.updated_at || thread.created_at;
        const groupName = getThreadGroup(dateStr, {
          today: TODAY,
          previous7Days: PREVIOUS_7_DAYS,
          older: OLDER,
          locale: localeCode
        });

        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(thread);
      }
    });

    if (groups[PINNED_CHATS || "Pinned Chats"].length === 0) {
      delete groups[PINNED_CHATS || "Pinned Chats"];
    }

    return groups;
  }, [filteredThreads, effectiveEnableGrouping, PINNED_CHATS, OLDER, PREVIOUS_7_DAYS, TODAY, localeCode]);

  return (
    <>
      <div className="threads-container">
        <div className="history-section">
          <div
            className="sidebar-section-title history-section-header clickable"
            onClick={toggleHistory}
          >
            <span>{HISTORY}</span>
            <img
              src={downArrow}
              className={`history-toggle-icon ${isCollapsedHistory ? "collapsed" : ""}`}
              alt="Toggle History"
            />
          </div>
          <div className={`history-content-wrapper ${isCollapsedHistory ? "collapsed" : ""}`}>
            {showSearchBar && (
              <div className="search-thread-bar-section">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClear={() => setSearchQuery("")}
                  placeholder={hasAnyThreads ? (SEARCH_HISTORY || "Search History") : (NO_HISTORY_AVAILABLE || "No history available")}
                  className={!hasAnyThreads ? "disabled-search" : ""}
                />
              </div>
            )}
            <div className="scrollable-threads thin-scroll">
              {Object.keys(groupedThreads).length > 0 ? (
                <div className="thread-card">
                  {Object.entries(groupedThreads).map(([groupName, threadsInGroup]) => {
                    const isGroupedView = effectiveEnableGrouping;
                    const isCollapsed = isGroupedView ? collapsedGroups[groupName] : false;

                    return (
                      <div key={groupName} className="thread-group-section">
                        {isGroupedView && (
                          <div
                            className="thread-group-header clickable"
                            onClick={() => toggleGroup(groupName)}
                            title={isCollapsed ? "Expand" : "Collapse"}
                          >
                            <div className="group-header-left">
                              <span>{groupName}</span>
                              <img
                                src={downArrow}
                                className={`group-toggle-icon ${isCollapsed ? "collapsed" : ""}`}
                                alt="Toggle Group"
                              />
                            </div>
                          </div>
                        )}
                        <div className={`thread-items ${isGroupedView ? 'grouped-view' : ''} ${isCollapsed ? "collapsed" : ""}`}>
                          {threadsInGroup.map((item, index) => {
                            const sessionIdField = threadFieldNames.sessionId;
                            const sessionId = item?.[sessionIdField];
                            const isSelected =
                              sessionManager.getSessionId(sessionStorageKey) === sessionId ||
                              selectedThreadId === sessionId;
                            const isHovered =
                              hoveredThreadId === sessionId &&
                              hoveredItemIndex === index;
                            const isOpen =
                              openedThreadId === sessionId &&
                              openedModelIndex === index;
                            const uniqueKey = `${sessionId}-${index}`;

                            return (
                              <div
                                key={uniqueKey}
                                ref={isSelected ? selectedThreadRef : null}
                                className={`thread-item-wrapper ${isSelected ? "selected" : ""
                                  }`}
                                onMouseEnter={() =>
                                  setHoveredThreadId(sessionId) ||
                                  setHoveredItemIndex(index)
                                }
                                onMouseLeave={() => {
                                  setHoveredThreadId(null);
                                  setHoveredItemIndex(null);
                                }}
                              >
                                <div className="text-thread-section">
                                  {isEditing && isOpen ? (
                                    <input
                                      ref={modalRef}
                                      type="text"
                                      value={editTitle}
                                      onChange={(e) => setEditTitle(e.target.value)}
                                      className="rename-input"
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleRenameItem(item);
                                        if (e.key === "Escape") setIsEditing(false);
                                      }}
                                      autoFocus
                                    />
                                  ) : (
                                    <div
                                      className="thread-item-content"
                                      onClick={() => {
                                        handleThreadClick(item);
                                      }}
                                    >
                                      <span className="thread-item-text">
                                        {item?.[threadFieldNames?.threadTitle] || item?.thread_title || item?.session_title}
                                      </span>

                                    </div>
                                  )}
                                </div>
                                {agentType !== "GMT" &&
                                  agentType !== "PROCUREMENT_AI" &&
                                  <div className="thread-action-button">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(
                                          e,
                                          sessionId,
                                          index,
                                          item?.[threadFieldNames.threadTitle]
                                        );
                                      }}
                                      className={`thread-item-more ${isHovered || (isOpen && showModalMore) || isMobile
                                        ? "visible"
                                        : ""
                                        }`}
                                    >
                                      <img
                                        src={threeDotsIcon}
                                        className="more-kebab-icon"
                                        alt={MORE_OPTIONS}
                                      />
                                    </button>
                                  </div>}

                                {isOpen && showModalMore && (
                                  <div
                                    ref={modalRef}
                                    className="thread-modal"
                                    style={{
                                      top: modalPosition.top,
                                      left: modalPosition.left,
                                    }}
                                  >
                                    <div className="global-action-dropdown dropdown-fade"
                                      style={{
                                        top: modalPosition.top,
                                        left: modalPosition.left,
                                        position: 'fixed'
                                      }}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPinTargetItem(item);
                                          setShowPinModal(true);
                                          setShowModalMore(false);
                                        }}
                                        className="global-action-item"
                                      >
                                        <img
                                          src={item?.pin_flag ? unpinIcon : pinIcon}
                                          alt={item?.pin_flag ? UNPIN : PIN}
                                        />
                                        {item?.pin_flag ? UNPIN : PIN}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          if (isMobile) {
                                            setRenameTargetItem(item);
                                            setEditTitle(item?.[threadFieldNames.threadTitle]);
                                            setShowMobileRenameModal(true);
                                            setShowModalMore(false);
                                            return;
                                          }
                                          setIsEditing(true);
                                          setShowModalMore(false);
                                          setEditTitle(item?.[threadFieldNames.threadTitle]);
                                        }}
                                        className="global-action-item"
                                      >
                                        <img
                                          src={renameIcon}
                                          alt={RENAME}
                                        />
                                        {RENAME}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteTargetItem(item);
                                          setShowDeleteModal(true);
                                          setShowModalMore(false);
                                        }}
                                        className="global-action-item delete"
                                      >
                                        <img
                                          src={deleteIcon}
                                          alt={DELETE}
                                        />
                                        {DELETE}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-threads-msg">
                  {searchQuery.trim() ? NO_THREADS_FOUND : NO_THREADS}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pin Confirmation Modal */}
      <Modal open={showPinModal} onClose={() => setShowPinModal(false)}>
        <div className="modal-inner-content">
          <div className="modal-title">{pinTargetItem?.pin_flag ? UNPIN_CHAT : PIN_CHAT}</div>
          <div className="modal-message">
            {pinTargetItem?.pin_flag ? UNPIN_CONFIRM_MSG : PIN_CONFIRM_MSG}
          </div>
          <div className="modal-buttons">
            <button
              onClick={() => {
                setShowPinModal(false);
                setPinTargetItem(null);
              }}
              className="button-style primary-grey-button"
            >
              {CANCEL}
            </button>
            <button
              onClick={async () => {
                await handlePinToggle(pinTargetItem);
                setShowPinModal(false);
                setPinTargetItem(null);
              }}
              className="button-style primary-button"
            >
              {pinTargetItem?.pin_flag ? UNPIN : PIN}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className="modal-inner-content">
          <div className="modal-title">{DELETE_CHAT}</div>
          <div className="modal-message">
            {DELETE_CONFIRM_MSG}
          </div>
          <div className="modal-buttons">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteTargetItem(null);
              }}
              className="button-style primary-grey-button"
            >
              {CANCEL}
            </button>
            <button
              onClick={async () => {
                await handleDelete(deleteTargetItem);
                setShowDeleteModal(false);
                setDeleteTargetItem(null);
              }}
              className="button-style primary-button"
            >
              {DELETE}
            </button>
          </div>
        </div>
      </Modal>

      {/* Rename Modal (Mobile/Header triggered) */}
      <Modal open={showMobileRenameModal} onClose={() => setShowMobileRenameModal(false)}>
        <div className="modal-inner-content">
          <div className="modal-title">{RENAME_CHAT}</div>
          <div className="modal-message">
            {RENAME_INSTRUCTION}
          </div>
          <div className="rename-body">
            <input
              type="text"
              className="rename-input-modal"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="modal-buttons">
            <button
              onClick={() => {
                setShowMobileRenameModal(false);
                setRenameTargetItem(null);
              }}
              className="button-style primary-grey-button"
            >
              {CANCEL}
            </button>
            <button
              onClick={() => {
                handleRenameItem(renameTargetItem);
                setShowMobileRenameModal(false);
                setRenameTargetItem(null);
              }}
              className="button-style primary-button"
            >
              {SAVE}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Threads;